import React, { useEffect, useState } from 'react';

import { Box, Col } from '@opendoor/atoms/box';
import { Image } from '@opendoor/atoms/image';
import { cn } from '@opendoor/atoms/utils';
import { IconMapPinFilled } from '@tabler/icons-react';
import { z } from 'zod';

import {
  googleStreetViewMetadataUrl,
  googleStreetViewUrl,
  type StreetViewAddress,
} from './streetView';

const streetViewMetadataSchema = z.object({ status: z.string() });

/**
 * Google's other failure statuses (`OVER_QUERY_LIMIT`, `REQUEST_DENIED`, ...) describe a problem
 * with the request rather than the place, so a quota blip must not swap real photos for maps.
 */
const NO_PANORAMA_STATUSES = new Set(['ZERO_RESULTS', 'NOT_FOUND']);

/** Hung metadata must not leave the skeleton up; after this the photo is treated as available. */
const METADATA_CHECK_TIMEOUT_MS = 5_000;

/**
 * Metadata is the cheap no-panorama check. It runs alongside the image request rather than before
 * it, so an address with a panorama waits on the slower of the two rather than on both in
 * sequence. The image URL also asks Google to 4xx a missing panorama, so a hung or wrong
 * metadata result still falls back instead of revealing a gray tile.
 */
const useStreetViewAvailability = (
  address: StreetViewAddress,
): 'checking' | 'available' | 'unavailable' => {
  const metadataUrl = googleStreetViewMetadataUrl(address);
  const [checked, setChecked] = useState<{ url: string; available: boolean } | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let didSettle = false;
    const settle = (available: boolean) => {
      if (didSettle) {
        return;
      }
      didSettle = true;
      setChecked({ url: metadataUrl, available });
    };

    // Cosmos still supports Firefox 102 / Safari 16, so this is a timer rather than
    // AbortSignal.any + AbortSignal.timeout.
    const timeoutId = window.setTimeout(() => {
      settle(true);
      controller.abort();
    }, METADATA_CHECK_TIMEOUT_MS);

    fetch(metadataUrl, { signal: controller.signal })
      .then((response) => response.json())
      .then((body) => streetViewMetadataSchema.parse(body))
      .then((metadata) => settle(!NO_PANORAMA_STATUSES.has(metadata.status)))
      // A network failure or a body we cannot parse does not say the address has no panorama,
      // so the image stays. Unmount abort is ignored; the timeout abort already settled.
      .catch(() => {
        if (!controller.signal.aborted) {
          settle(true);
        }
      })
      .finally(() => window.clearTimeout(timeoutId));

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [metadataUrl]);

  if (checked?.url !== metadataUrl) {
    return 'checking';
  }
  return checked.available ? 'available' : 'unavailable';
};

interface StreetViewPhotoProps {
  address: StreetViewAddress;
  /** Static map shown when the address has no Street View imagery. */
  mapUrl: string | null;
  /**
   * Rendered slot size (CSS pixels) per breakpoint. Each request matches its slot's aspect so
   * `object-cover` does not crop Google's baked-in attribution (Maps TOS). `mobile` is the default
   * `<img>`; `desktop` is served when `desktopMedia` matches.
   */
  size: { mobile: { width: number; height: number }; desktop: { width: number; height: number } };
  /**
   * Media query that selects the desktop request. Defaults to Tailwind `sm`. Hub cards pass `md`
   * because their slot stays 5:2 until then.
   */
  desktopMedia?: string;
}

/** Tailwind `sm` breakpoint: the default desktop source wins at and above this width. */
const DEFAULT_DESKTOP_MEDIA = '(min-width: 640px)';

// jsdom leaves currentSrc empty, so fall back to src.
const activeUrl = (img: HTMLImageElement): string => img.currentSrc || img.src;

/**
 * Street View of an address, falling back to the caller's static map, then a pin. Fills its
 * parent; the parent must give it a box. Callers pass their rendered slot size per breakpoint, so
 * one component serves every photo slot that needs the cascade.
 */
export const StreetViewPhoto: React.FC<StreetViewPhotoProps> = ({
  address,
  mapUrl,
  size,
  desktopMedia = DEFAULT_DESKTOP_MEDIA,
}) => {
  const [failedUrls, setFailedUrls] = useState<ReadonlySet<string>>(new Set());
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null);
  const [failedMapUrl, setFailedMapUrl] = useState<string | null>(null);
  // Returning the same set when the url is already known keeps the ref callback below from
  // re-rendering itself in a loop.
  const markFailed = (url: string) =>
    setFailedUrls((failed) => (failed.has(url) ? failed : new Set(failed).add(url)));

  const streetView = useStreetViewAvailability(address);
  const mobileUrl = googleStreetViewUrl({ address, ...size.mobile });
  const desktopUrl = googleStreetViewUrl({ address, ...size.desktop });
  // Both URLs render the same panorama, so a dead URL for one breakpoint dead-ends both.
  const usable =
    streetView !== 'unavailable' && !failedUrls.has(mobileUrl) && !failedUrls.has(desktopUrl);

  if (!usable) {
    // A map that fails to load (quota, bad key, network) still has the pin beneath it; tracking
    // the failed URL lets a different map URL retry rather than inheriting the failure.
    if (mapUrl && mapUrl !== failedMapUrl) {
      return (
        <Image
          alt=""
          src={mapUrl}
          className="h-full w-full object-cover"
          onError={() => setFailedMapUrl(mapUrl)}
        />
      );
    }
    return (
      <Col className="h-full w-full items-center justify-center bg-od-bg-secondary">
        <IconMapPinFilled size={24} className="text-od-content-primary" />
      </Col>
    );
  }

  const ready = streetView === 'available' && (loadedUrl === mobileUrl || loadedUrl === desktopUrl);

  // An image already decoded when React attaches (browser cache, or hydrated from the server) has
  // fired its load event before there was a handler to catch it, which would strand the skeleton
  // over a photo that is sitting right there.
  const settleIfAlreadyLoaded = (img: HTMLImageElement | null) => {
    if (!img?.complete) {
      return;
    }
    if (img.naturalWidth > 0) {
      setLoadedUrl(activeUrl(img));
    } else {
      markFailed(activeUrl(img));
    }
  };
  const handlePhotoLoad = (event: React.SyntheticEvent<HTMLImageElement>) =>
    setLoadedUrl(activeUrl(event.currentTarget));
  const handlePhotoError = (event: React.SyntheticEvent<HTMLImageElement>) =>
    markFailed(activeUrl(event.currentTarget));

  return (
    <Box className="relative h-full w-full">
      <Box
        aria-hidden
        className={cn('absolute inset-0 skeleton bg-od-bg-tertiary', ready && 'hidden')}
      />
      <Image
        ref={settleIfAlreadyLoaded}
        alt=""
        src={mobileUrl}
        sources={[{ srcSet: desktopUrl, media: desktopMedia }]}
        pictureClassName="block h-full w-full"
        width={size.mobile.width}
        height={size.mobile.height}
        onLoad={handlePhotoLoad}
        onError={handlePhotoError}
        className={cn('h-full w-full object-cover', !ready && 'invisible')}
      />
    </Box>
  );
};
